/**
 * Fruit stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const fruitStickersPack: ToilePack = {
    slug: 'fruit-stickers',
    category: 'sticker',
    name: 'Fruit stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'strawberry', label: 'Strawberry', subject: 'a strawberry with a leafy top and a small bow'},
        {key: 'lemon', label: 'Lemon slice', subject: 'a lemon slice with its segments showing'},
        {key: 'cherries', label: 'Cherries', subject: 'two cherries on a joined stem with a leaf'},
    ],
    styles: [
        {key: 'kawaii', label: 'Kawaii', style: 'kawaii sticker illustration, rounded shapes, tiny smiling faces, soft thick outlines', palette: 'Pastel pink, mint, lilac and cream with dark outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'doodle', label: 'Hand-drawn', style: 'hand-drawn marker doodle sticker, loose line, flat wash fills', palette: 'Two or three muted colours with a black line', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a watermelon instead', 'for a water bottle', 'as a fruit salad set'],
    directionHint: 'try: a watermelon instead',
    seo: {
        title: 'Fruit Stickers: Strawberry, Lemon, Cherries',
        metaDescription: 'Nine cute fruit stickers — a strawberry with a bow, a lemon slice, cherries on a stem — in kawaii, bold cartoon, and hand-drawn styles.',
        h1: 'Fruit stickers: strawberry, lemon slice, cherries',
        tagline: 'Nine stickers, three fruits, three styles each.',
        intro: 'A strawberry with a leafy top and a small bow, a lemon slice with its segments showing, and two cherries on a joined stem — three fruit stickers, die-cut and ready to print. Each rendered kawaii, bold cartoon, and loose hand-drawn. Ask for a different fruit, a smoothie scene, or a picnic composition, and the same three styles carry it.',
        useCases: [
            'a water bottle or lunchbox',
            'a planner or journal page',
            'a phone case sticker sheet',
            'a kitchen or recipe notebook',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine fruit stickers — a strawberry, a lemon slice, and cherries — in kawaii, bold cartoon, and hand-drawn finishes.'},
            {q: 'Can I get a different fruit?', a: 'Yes — the direction field takes any variation, including a different fruit, a smoothie scene, or a picnic composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full A4 sheet.'},
            {q: 'Are these photos of real fruit?', a: 'No — every sticker is an original cartoon illustration, not a photograph.'},
        ],
    },
}
